// Typed client for sstk-studio-api's Code workspace endpoints
// (app/routers/scripts.py, migration 0028_script_documents.sql):
//
//   GET    /projects/{id}/scripts               -> ScriptDocument[], full documents
//   GET    /projects/{id}/scripts/{scriptId}     -> ScriptDocument
//   PUT    /projects/{id}/scripts/{scriptId}     -> body: ScriptDocument, upserts
//   DELETE /projects/{id}/scripts/{scriptId}
//
// Mirrors studioApiWeaponPersistence.ts's shape (a project's scripts are a
// roster of independently-named, saved, deleted documents, not one shared
// blob - see the migration's own header comment), minus a publish step: a
// runOnStart script only ever executes in the Studio editor's own Preview
// today - script_documents has no publish/snapshot path onto a published
// game the way weapon_documents does, so a published game does not run any
// script yet. This is a plain list/get/put/delete client, nothing more.
import { apiDelete, apiGet, apiPut } from './api';

export interface ScriptDocument {
  id: string;
  name: string;
  source: string;
  runOnStart: boolean;
}

const listPath = (projectId: string) => `/projects/${projectId}/scripts`;
const itemPath = (projectId: string, scriptId: string) => `/projects/${projectId}/scripts/${scriptId}`;

/** Loads every script in the project's roster - the whole set, not one at a
 * time: HollowScript's `run()` resolves an `import` synchronously
 * (`resolveImport?: (path) => string | undefined`), so the runtime needs
 * every other script's source already in memory before it can execute
 * anything that might import one. The backend already returns full
 * documents from this one endpoint (see the router's own comment), so no
 * per-item follow-up fetch is needed. */
export async function listScripts(projectId: string): Promise<ScriptDocument[]> {
  return (await apiGet<ScriptDocument[] | null>(listPath(projectId))) ?? [];
}

export async function saveScript(projectId: string, script: ScriptDocument): Promise<ScriptDocument> {
  await apiPut(itemPath(projectId, script.id), script);
  // Echoes back what was sent rather than re-fetching, matching
  // studioApiWeaponPersistence.ts's saveWeapon convention.
  return script;
}

export async function deleteScript(projectId: string, scriptId: string): Promise<void> {
  await apiDelete(itemPath(projectId, scriptId));
}
